import LinkedInIcon from '@mui/icons-material/LinkedIn'
import GitHubIcon from '@mui/icons-material/GitHub'
import { ReactElement } from 'react'
import { SITE } from '../constants'

export type ResumeData = {
    name: string
    position: string
    location: string
    socials: SocialsData[]
    summary: string
    certifications: TimelineData[]
    education: TimelineData[]
    workHistory: TimelineData[]
    otherExperience: TimelineData[]
    projects: Project[]
    earlyProjects: VideoProject[]
}

export type TimelineData = {
    name: string
    /** Job title, if known */
    role?: string
    startDate?: number
    endDate?: number
    description: string
    highlights?: string[]
}

export type SocialsData = {
    text: string
    link: string
    icon: ReactElement
}

export type Project = {
    name: string
    kind: 'Client website' | 'Side project'
    description: string
    /** Shorter, non-technical description for the homepage feature */
    summary?: string
    url: string
    /** Screenshot, 16:10 */
    image: string
    tags: string[]
}

export type VideoProject = {
    name: string
    description: string
    /** YouTube video id */
    link: string
}

const resumeData: ResumeData = {
    name: `Thomas Viles Jr`,
    position: `Full-Stack Software Engineer & AWS Consultant`,
    location: SITE.location,
    socials: [
        {
            text: `LinkedIn`,
            link: SITE.linkedin,
            icon: <LinkedInIcon />
        },
        {
            text: `Github`,
            link: SITE.github,
            icon: <GitHubIcon />
        },
    ],
    summary:
    `Full-stack software engineer working professionally since 2018, focused on React, TypeScript, Python and AWS serverless architecture.
    I've built and owned production applications, internal tools and APIs, introduced automated testing and CI/CD,
    and served as the go-to AWS resource on my teams. U.S. Army veteran, Flatiron School graduate, and AWS Certified
    Solutions Architect and Developer.`,
    // TODO(Thomas): confirm current certification status/expiry. The previous site listed
    // Solutions Architect as valid 2022–2025 and Developer as 2023–2026, so one may need renewal.
    certifications: [
        {
            name: `AWS Certified Solutions Architect – Associate`,
            description: `Designing scalable, secure and fault-tolerant systems on AWS.`
        },
        {
            name: `AWS Certified Developer – Associate`,
            description: `Developing, deploying and maintaining applications on AWS.`
        },
    ],
    education: [
        {
            name: `Flatiron School`,
            role: `Software Engineering`,
            startDate: 2017,
            endDate: 2018,
            description: `Immersive full-stack software engineering program covering JavaScript, React, Redux and Ruby on Rails.`
        },
        {
            name: `Indiana University – Purdue University Indianapolis (IUPUI)`,
            role: `Coursework in Biology`,
            startDate: 2010,
            endDate: 2014,
            description: `Also served as a peer mentor for Anatomy & Physiology.`
        },
    ],
    // TODO(Thomas): add job titles (role) for each position.
    workHistory: [
        {
            name: `Black Widow Imaging`,
            startDate: 2024,
            description: `Full-stack development, cloud engineering and test automation.`,
            highlights: [
                `Led an extensive refactor of the React web and React Native mobile applications, migrating both to TypeScript and restructuring them into consistent, maintainable codebases`,
                `Implemented Amazon Cognito User Pools and Identity Pools, replacing custom role and permission handling in DynamoDB with AWS-managed identity and IAM-based access`,
                `Analyzed DynamoDB access patterns and indexing, identified redundant writes, and developed an optimized approach with significant projected infrastructure savings`,
                `Introduced automated testing with Jest (unit) and Cypress (end-to-end), and improved logging and debugging practices`,
                `Built event-driven AWS features, including near-real-time updates over MQTT and AWS IoT Core, and media upload and processing workflows with S3, Lambda, API Gateway and SQS`,
                `Built Python and Django back-end services and React admin tools that let non-developer teams handle configuration, user administration and routine operations`,
                `Set up AWS CodePipeline CI/CD and served as the team's subject-matter expert for AWS services and cloud architecture`,
                `Used AI tools to speed up research, design and development`,
            ],
        },
        {
            name: `Chronotrack / Life Time`,
            startDate: 2022,
            endDate: 2024,
            description: `Event timing software.`,
            highlights: [
                `Contributed to the migration from a legacy PHP web application to a modern React application, improving user experience and performance`,
                `Subject-matter expert for Lambda-based, event-driven microservices in Node.js`,
                `Resolved long-standing bugs and shipped new features across the platform`,
                `Worked with AWS, React, TypeScript, RTK Query, Docker, Kubernetes, Jest, Cypress and PHP`,
            ],
        },
        {
            name: `Ontario Systems / Finvi`,
            startDate: 2018,
            endDate: 2022,
            description: `Financial software.`,
            highlights: [
                `Key contributor to the transition to SaaS, specializing in API feature development`,
                `Subject-matter expert for ConnectAPI V2`,
                `Built features across new caching layers and legacy financial code`,
                `Mentored interns through the company mentorship program`,
            ],
        },
    ],
    otherExperience: [
        {
            name: `U.S. Army`,
            startDate: 2014,
            endDate: 2016,
            role: `Specialist (SPC)`,
            description: `Served as a Specialist, working alongside newer privates and helping them get up to speed, with an emphasis on discipline and precise execution.`
        },
    ],
    projects: [
        {
            name: 'Rietow Family Dentistry',
            kind: 'Client website',
            description: 'Website for a family and cosmetic dental practice in Greenwood, Indiana: services, team, discount plans and downloadable new-patient forms, built mobile-first with local SEO and structured data so patients can find the office and book a visit.',
            summary: 'A mobile-friendly website for a family and cosmetic dental practice in Greenwood, Indiana, with services, team, discount plans and new-patient forms, set up so patients can find the office and book a visit.',
            url: 'https://www.rietowfamilydentistry.com',
            image: require('../media/projects/rietow-family-dentistry.jpg'),
            tags: ['React', 'TypeScript', 'Material UI', 'S3', 'CloudFront', 'CodeBuild'],
        },
        {
            name: 'Concrete Calculator',
            kind: 'Side project',
            description: 'A concrete volume calculator for slabs, columns, walls, footers, steps and curb & gutter, with mixed feet, inch and meter inputs. Built to make a common job-site estimate quick on a phone.',
            url: 'https://concretecalc.tviles.com',
            image: require('../media/projects/concrete-calculator.jpg'),
            tags: ['React', 'TypeScript', 'Material UI', 'S3', 'CloudFront'],
        },
    ],
    earlyProjects: [
        {
            name: `Ruby CLI Project`,
            description: `My first bootcamp project (January 2018), written in plain Ruby before learning Rails.`,
            link: `DH6cWyXXJIo`,
        },
        {
            name: `Sinatra Project`,
            description: `My first MVC application, a makeshift dentist app covering authentication, CRUD and ERB views (February 2018).`,
            link: `zo3IpJk9qLM`,
        },
        {
            name: `Fantasy Football Rails Project`,
            description: `A miniature fantasy football application in Ruby on Rails using RESTful APIs (March 2018). Shortly after, I started my first professional engineering role.`,
            link: `dSMkriLCt8Y`,
        },
    ]
}

export default resumeData
